import { ChevronDown } from 'lucide-react';
import dynamic from 'next/dynamic';

import { AccordionContent, AccordionItem, AccordionTrigger } from '@natu/ui/Accordion';
import { NavigationMenuItem, NavigationMenuTrigger } from '@natu/ui/NavigationMenu';
import { Typography } from '@natu/ui/Typography';
import { cn } from '@natu/utils/cn';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import { BlokItem, SBProps, sbEditable } from '../../../../utils';
import { DecoratorPosition } from './components/NavigationMegaMenuItemDecorators';

const NavigationMenuContent = dynamic(() =>
  import('@natu/ui/NavigationMenu').then(mod => mod.NavigationMenuContent),
);

const NavigationMegaMenuItemDecorators = dynamic(() =>
  import('./components/NavigationMegaMenuItemDecorators').then(
    mod => mod.NavigationMegaMenuItemDecorators,
  ),
);

const DynamicRender = dynamic(() => import('../../../../utils').then(mod => mod.DynamicRender));

interface SBNavigationMegaMenuItemProps {
  body?: BlokItem[];
  bottomContent?: BlokItem[];
  label?: string;
  decoratorPosition?: DecoratorPosition;
}

export const SBNavigationMegaMenuItem = ({ blok }: SBProps<SBNavigationMegaMenuItemProps>) => {
  const { body, label, _uid, bottomContent, decoratorPosition } = blok;

  const isMegaMenu = isArrayWithLength(body);

  return (
    <>
      {/* Desktop navigation  */}
      <NavigationMenuItem className="hidden lg:block" {...sbEditable(blok)}>
        <NavigationMenuTrigger className="p-1">
          <Typography variant="text-md" className="font-normal text-gray-600" component="span">
            {label}
          </Typography>
          {isMegaMenu && (
            <ChevronDown
              className="relative top-px ml-1 h-3 w-3 transition duration-300 group-data-[state=open]:rotate-180"
              aria-hidden="true"
            />
          )}
        </NavigationMenuTrigger>
        {isMegaMenu && (
          <NavigationMenuContent
            className={cn('relative w-screen md:w-screen md:max-w-max lg:w-screen')}
          >
            <div className="relative z-10 p-8">
              <DynamicRender data={body} />
            </div>
            {isArrayWithLength(bottomContent) && (
              <div className="bg-naturaily-azureGray w-full py-[12px]">
                <DynamicRender data={bottomContent} />
              </div>
            )}

            <NavigationMegaMenuItemDecorators decoratorPosition={decoratorPosition} />
          </NavigationMenuContent>
        )}
      </NavigationMenuItem>

      {/* Mobile navigation  */}
      <AccordionItem className="lg:hidden" value={_uid} {...sbEditable(blok)}>
        <AccordionTrigger className="py-0">
          <Typography
            variant="text-lg"
            component="span"
            className="group-data-[state=open]:text-primary-600 text-gray-600 transition-colors"
          >
            {label}
          </Typography>
          {isMegaMenu && (
            <ChevronDown
              className="ease-naturaily duration-600 group-data-[state=open]:text-primary-600 h-4 w-4 shrink-0 transition-all group-data-[state=open]:rotate-180"
              aria-hidden="true"
            />
          )}
        </AccordionTrigger>
        <AccordionContent className="p-0">
          <div className="mt-4">
            <DynamicRender data={body} />
          </div>
          {isArrayWithLength(bottomContent) && (
            <div className="xl:bg-naturaily-azureGray mt-4 w-fit py-[12px] md:mt-6 lg:mt-12 xl:w-full">
              <DynamicRender data={bottomContent} />
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </>
  );
};
